import React from "react";

/**
 * Small bar chart for a short, ordered series — weekly load, calories per day,
 * sleep per night.
 *
 * `null` values render as a muted "sin registro" bar rather than a zero-height
 * one, because a day nobody logged and a day of genuinely zero intake are
 * different facts and the club acts differently on each.
 *
 * `reference` draws the dashed threshold line the series should be read
 * against (the daily calorie reference, 150% of average load).
 */
const Bars = ({
  values = [],
  labels = [],
  reference = null,
  referenceLabel,
  height = 78,
  colorFor,
  label,
}) => {
  if (values.length === 0) {
    return <p className="py-4 text-[12px] text-peri-300">Sin registros en el periodo.</p>;
  }

  const numeric = values.filter((v) => typeof v === "number" && Number.isFinite(v) && v > 0);
  const max = Math.max(...numeric, reference ?? 0, 1);
  const slot = 100 / values.length;
  const plotHeight = height - 18;

  return (
    <svg
      viewBox={`0 0 100 ${height}`}
      width="100%"
      height={height}
      role="img"
      aria-label={label || "Serie por día"}
      preserveAspectRatio="none"
    >
      {values.map((value, index) => {
        const missing = typeof value !== "number" || !Number.isFinite(value) || value <= 0;
        // Missing days keep a 2px stub so the gap is visible as a gap rather
        // than as empty space the eye reads as the end of the series.
        const barHeight = missing ? 2 : (value / max) * plotHeight;
        const fill = missing ? "#e3e0e0" : colorFor ? colorFor(value) : "#4274d9";
        return (
          <rect
            key={index}
            x={index * slot + slot * 0.2}
            y={height - 14 - barHeight}
            width={slot * 0.6}
            height={barHeight}
            rx="1.2"
            fill={fill}
          />
        );
      })}

      {reference !== null && reference > 0 && (
        <line
          x1="0"
          y1={height - 14 - (reference / max) * plotHeight}
          x2="100"
          y2={height - 14 - (reference / max) * plotHeight}
          stroke="#bc1d01"
          strokeWidth="0.6"
          strokeDasharray="2 2"
        >
          {referenceLabel && <title>{referenceLabel}</title>}
        </line>
      )}

      {labels.map((text, index) => (
        <text
          key={`${text}-${index}`}
          x={index * slot + slot / 2}
          y={height - 3}
          textAnchor="middle"
          fontSize="5"
          fill="#7692d1"
          fontFamily="Poppins, sans-serif"
        >
          {text}
        </text>
      ))}
    </svg>
  );
};

export default Bars;
